import { useEffect, useState } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Sun, AlertCircle, Clock, Target, DollarSign,
  Calendar, Bell, ArrowRight, TrendingUp, CheckCircle2
} from "lucide-react";

function StatCard({ icon: Icon, label, value, color = "#0A1628", testid }) {
  return (
    <Card className="border-0 shadow-sm" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
            <p className="text-2xl font-bold mt-1" style={{ color }}>{value}</p>
          </div>
          <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + "15" }}>
            <Icon className="h-5 w-5" style={{ color }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function LeadRow({ lead, accent }) {
  return (
    <div className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors group">
      <div className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: accent }} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#0A1628] truncate">{lead.titulo}</p>
        <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
          {lead.cliente_nome && <span className="truncate">{lead.cliente_nome}</span>}
          {lead.valor_estimado > 0 && (
            <span className="font-mono text-emerald-600">
              R$ {Number(lead.valor_estimado).toLocaleString("pt-BR", { minimumFractionDigits: 0 })}
            </span>
          )}
          {lead.dias_parado > 0 && <Badge variant="outline" className="text-[9px] h-4 px-1">{lead.dias_parado}d parado</Badge>}
        </div>
      </div>
      <Link to="/comercial/crm">
        <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity h-7 text-xs">
          Ver <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
      </Link>
    </div>
  );
}

function Section({ title, icon: Icon, color, items, emptyMsg, testid }) {
  return (
    <Card className="border-0 shadow-sm" data-testid={testid}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2" style={{ color }}>
          <Icon className="h-4 w-4" /> {title} <Badge variant="outline" className="ml-auto text-xs">{items.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 max-h-72 overflow-y-auto">
        {items.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-4">{emptyMsg}</p>
        ) : (
          items.map((l) => <LeadRow key={l.id} lead={l} accent={color} />)
        )}
      </CardContent>
    </Card>
  );
}

export default function MeuDiaPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = sessionStorage.getItem("luvcorp_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/crm/meu-dia`, { headers })
      .then(r => setData(r.data))
      .catch(() => toast.error("Erro ao carregar Meu Dia"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <MainLayout><div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#0A1628]" /></div></MainLayout>;
  if (!data) return <MainLayout><p className="text-center text-slate-500 py-12">Erro ao carregar</p></MainLayout>;

  const hora = new Date().getHours();
  const saudacao = hora < 12 ? "Bom dia" : hora < 18 ? "Boa tarde" : "Boa noite";
  const urgentes = data.sla_vermelho.length + data.acoes_vencidas.length;

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="meu-dia-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold text-[#0A1628] flex items-center gap-2">
              <Sun className="h-6 w-6 text-[#C9A84C]" />
              {saudacao}, {data.usuario_nome?.split(" ")[0] || "vendedor"}!
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {new Date().toLocaleDateString("pt-BR", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
            </p>
          </div>
          <Link to="/comercial/crm"><Button className="bg-[#0A1628]" size="sm" data-testid="btn-ir-crm"><Target className="h-4 w-4 mr-1" />Ir ao CRM</Button></Link>
        </div>

        {urgentes > 0 && (
          <Card className="border-l-4 border-red-500 bg-red-50/50" data-testid="alerta-urgente-banner">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-red-700">Voce tem {urgentes} item(ns) que precisam de atencao imediata</p>
                <p className="text-xs text-red-600">Leads parados ha muitos dias ou com acoes vencidas. Reveja abaixo.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Target} label="Leads ativos" value={data.total_leads_ativos} color="#3B82F6" testid="kpi-leads-ativos" />
          <StatCard icon={DollarSign} label="Pipeline" value={`R$ ${Number(data.valor_pipeline).toLocaleString("pt-BR", { minimumFractionDigits: 0 })}`} color="#10B981" testid="kpi-pipeline" />
          <StatCard icon={Bell} label="Nao lidas" value={data.notificacoes_nao_lidas} color="#F59E0B" testid="kpi-nao-lidas" />
          <StatCard icon={AlertCircle} label="SLA vermelho" value={data.sla_vermelho.length} color="#EF4444" testid="kpi-sla-vermelho" />
        </div>

        {/* Seções */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Section title="Acoes vencidas (atrasadas)" icon={AlertCircle} color="#EF4444" items={data.acoes_vencidas} emptyMsg="Nenhuma acao vencida. Otimo!" testid="section-acoes-vencidas" />
          <Section title="Acoes para hoje" icon={Calendar} color="#3B82F6" items={data.acoes_hoje} emptyMsg="Sem agendamentos hoje" testid="section-acoes-hoje" />
          <Section title="SLA vermelho (urgentes)" icon={AlertCircle} color="#EF4444" items={data.sla_vermelho} emptyMsg="Nenhum lead urgente" testid="section-sla-vermelho" />
          <Section title="SLA amarelo (atencao)" icon={Clock} color="#F59E0B" items={data.sla_amarelo} emptyMsg="Tudo no prazo" testid="section-sla-amarelo" />
        </div>

        {data.leads_em_dia.length > 0 && (
          <Section title="Demais leads em dia" icon={CheckCircle2} color="#10B981" items={data.leads_em_dia} emptyMsg="Nada por aqui" testid="section-em-dia" />
        )}
      </div>
    </MainLayout>
  );
}
